import { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Badge } from "../ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { toast } from "sonner";
import { DollarSign, CreditCard } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function QuotesTab({ quotes, isLoading, getStatusBadge, formatDate, fetchAllData }) {
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [quotedAmount, setQuotedAmount] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);

  const updateQuoteStatus = async (quoteId, status) => {
    try { await axios.patch(`${API}/quotes/${quoteId}`, { status }); toast.success("Updated"); fetchAllData(); } catch (e) { toast.error("Failed"); }
  };

  const setQuoteAmount = async (quoteId) => {
    if (!quotedAmount || parseFloat(quotedAmount) <= 0) { toast.error("Invalid amount"); return; }
    setIsUpdating(true);
    try { await axios.patch(`${API}/quotes/${quoteId}`, { quoted_amount: parseFloat(quotedAmount), status: "quoted" }); toast.success("Amount set"); setSelectedQuote(null); setQuotedAmount(""); fetchAllData(); } catch (e) { toast.error("Failed"); } finally { setIsUpdating(false); }
  };

  const createPaymentLink = async (quote) => {
    if (!quote.quoted_amount) { toast.error("Set amount first"); return; }
    setIsUpdating(true);
    try {
      const res = await axios.post(`${API}/payments/checkout`, { quote_id: quote.id, origin_url: window.location.origin });
      if (res.data.url) { await navigator.clipboard.writeText(res.data.url); toast.success("Payment link copied!"); await axios.patch(`${API}/quotes/${quote.id}`, { stripe_payment_link: res.data.url }); fetchAllData(); }
    } catch (e) { toast.error("Failed"); } finally { setIsUpdating(false); }
  };

  if (isLoading) return <div className="text-center py-12 text-zinc-500">Loading...</div>;
  if (quotes.length === 0) return <div className="text-center py-12 text-zinc-500 bg-white rounded-sm border">No quotes yet</div>;

  return (
    <div className="bg-white rounded-sm border border-zinc-200 overflow-hidden overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="bg-zinc-50">
            <TableHead className="font-heading uppercase text-xs">Customer</TableHead>
            <TableHead className="font-heading uppercase text-xs">Details</TableHead>
            <TableHead className="font-heading uppercase text-xs">Status</TableHead>
            <TableHead className="font-heading uppercase text-xs">Amount</TableHead>
            <TableHead className="font-heading uppercase text-xs">Date</TableHead>
            <TableHead className="font-heading uppercase text-xs">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {quotes.map((q) => (
            <TableRow key={q.id}>
              <TableCell>
                <p className="font-medium">{q.customer_name}</p>
                <p className="text-sm text-zinc-500">{q.email}</p>
              </TableCell>
              <TableCell>
                <p className="text-sm">Type: {q.machine_type}</p>
                <p className="text-sm">Qty: {q.quantity}</p>
              </TableCell>
              <TableCell><Badge className={getStatusBadge(q.status)}>{q.status}</Badge></TableCell>
              <TableCell>{q.quoted_amount ? <span className="font-medium text-green-600">${q.quoted_amount.toFixed(2)}</span> : <span className="text-zinc-400">Not set</span>}</TableCell>
              <TableCell className="text-sm text-zinc-500">{formatDate(q.created_at)}</TableCell>
              <TableCell>
                <div className="flex gap-2">
                  <Dialog open={selectedQuote?.id === q.id} onOpenChange={(open) => !open && setSelectedQuote(null)}>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm" onClick={() => { setSelectedQuote(q); setQuotedAmount(q.quoted_amount?.toString() || ""); }}>
                        <DollarSign className="h-4 w-4" />
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader><DialogTitle className="font-heading uppercase">Set Amount</DialogTitle></DialogHeader>
                      <div className="space-y-4 mt-4">
                        <div>
                          <Label className="text-xs uppercase">Amount (USD)</Label>
                          <Input type="number" value={quotedAmount} onChange={(e) => setQuotedAmount(e.target.value)} className="mt-1" />
                        </div>
                        <Button onClick={() => setQuoteAmount(q.id)} disabled={isUpdating} className="w-full bg-blue-600 hover:bg-blue-700">
                          {isUpdating ? "Saving..." : "Save"}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                  <Button variant="outline" size="sm" disabled={!q.quoted_amount || isUpdating} onClick={() => createPaymentLink(q)}>
                    <CreditCard className="h-4 w-4" />
                  </Button>
                  <Select value={q.status} onValueChange={(v) => updateQuoteStatus(q.id, v)}>
                    <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="contacted">Contacted</SelectItem>
                      <SelectItem value="quoted">Quoted</SelectItem>
                      <SelectItem value="paid">Paid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
